/**
 * Tri à DEUX niveaux réutilisable pour les tables à colonnes numériques (classement
 * Elo, clubs…). Modèle « deux dernières colonnes cliquées » : un tap sur une colonne
 * la passe en PRIMAIRE (décroissant) et fait DESCENDRE l'ancienne primaire en
 * SECONDAIRE — celle-ci départage alors les lignes à égalité sur la primaire. Re-tap
 * sur la primaire inverse son sens. Pas de modificateur clavier requis (mobile-first).
 * Repères « 1 / 2 » + carets sur les en-têtes. Fonctions PURES → testées.
 */
export interface SortState<K extends string> {
  primary: K;
  primaryDir: 1 | -1; // -1 = décroissant
  secondary: K | null;
  secondaryDir: 1 | -1;
}

export function createSortState<K extends string>(primary: K, dir: 1 | -1 = -1): SortState<K> {
  return { primary, primaryDir: dir, secondary: null, secondaryDir: -1 };
}

/** Applique un clic d'en-tête (cf. modèle ci-dessus). Mute l'état en place. */
export function cycleSort<K extends string>(s: SortState<K>, key: K): void {
  if (key === s.primary) {
    s.primaryDir = s.primaryDir === -1 ? 1 : -1;
    return;
  }
  s.secondary = s.primary;
  s.secondaryDir = s.primaryDir;
  s.primary = key;
  s.primaryDir = -1; // une nouvelle primaire démarre en décroissant (meilleur en haut)
}

/** Rang de la colonne dans le tri courant : 1 = primaire, 2 = secondaire, 0 = aucun. */
export function sortRank<K extends string>(s: SortState<K>, key: K): 0 | 1 | 2 {
  return key === s.primary ? 1 : key === s.secondary ? 2 : 0;
}

function dirOf<K extends string>(s: SortState<K>, key: K): 1 | -1 | 0 {
  return key === s.primary ? s.primaryDir : key === s.secondary ? s.secondaryDir : 0;
}

export function sortAria<K extends string>(
  s: SortState<K>,
  key: K,
): "ascending" | "descending" | "none" {
  if (key !== s.primary) return "none";
  return s.primaryDir === -1 ? "descending" : "ascending";
}

/** Classe d'état pour l'en-tête : "on" (primaire), "on2" (secondaire) ou "". */
export function sortClass<K extends string>(s: SortState<K>, key: K): string {
  const r = sortRank(s, key);
  return r === 1 ? "on" : r === 2 ? "on2" : "";
}

/** Repère «  ▾¹ » / «  ▴² » (caret selon le sens + exposant du rang), vide si non trié. */
export function sortMark<K extends string>(s: SortState<K>, key: K): string {
  const rank = sortRank(s, key);
  if (!rank) return "";
  const caret = dirOf(s, key) === -1 ? "▾" : "▴";
  return ` ${caret}<sup class="srt-n">${rank}</sup>`;
}

/**
 * Trie une COPIE de `rows` : primaire, puis secondaire pour les ex æquo, puis un
 * départage stable fourni par l'appelant. `valueOf(row, key)` renvoie un NOMBRE.
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  s: SortState<K>,
  valueOf: (row: T, key: K) => number,
  tiebreak: (a: T, b: T) => number,
): T[] {
  return [...rows].sort((a, b) => {
    const p = (valueOf(a, s.primary) - valueOf(b, s.primary)) * s.primaryDir;
    if (p !== 0) return p;
    if (s.secondary) {
      const sec = (valueOf(a, s.secondary) - valueOf(b, s.secondary)) * s.secondaryDir;
      if (sec !== 0) return sec;
    }
    return tiebreak(a, b);
  });
}
